'use client'

import ConfirmModal from '@/components/confirm-modal'
import { cancelSalesFlowRun, type ChatFlowRun } from '@/lib/api/sales/flows-api'
import { SALES_FLOW_SYSTEM_KEYS } from '@mediardex/contracts'
import { useState, type ReactNode } from 'react'
import { MdAccountTree, MdClose, MdScheduleSend } from 'react-icons/md'
import { toast } from 'react-toastify'

const WAITING_LABELS: Record<string, string> = {
    REPLY: 'aguardando resposta',
    TIMER: 'aguardando',
    WINDOW: 'aguardando o horário de envio',
    RATE_LIMIT: 'aguardando o limite de envios do setor',
}

const formatDateTime = (iso?: string | null) =>
    iso
        ? new Date(iso).toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }).replace(',', '')
        : ''

const preview = (text: string, size = 70) => {
    const flat = text.replace(/\s+/g, ' ').trim()
    return flat.length > size ? `${flat.slice(0, size - 1)}…` : flat
}

function describe(run: ChatFlowRun): { icon: ReactNode; title: string; detail: string } {
    if (run.flow.systemKey === SALES_FLOW_SYSTEM_KEYS.SCHEDULED_MESSAGE) {
        const metadata = run.triggerMetadata ?? {}
        return {
            icon: <MdScheduleSend className="text-lg text-info" />,
            title: `Mensagem agendada para ${formatDateTime(metadata.scheduledAt)}`,
            detail: preview(String(metadata.message ?? '')),
        }
    }
    const waiting = run.status === 'WAITING' && run.waitingFor ? WAITING_LABELS[run.waitingFor] ?? 'aguardando' : 'executando'
    const until = run.status === 'WAITING' && run.resumeAt ? ` até ${formatDateTime(run.resumeAt)}` : ''
    return {
        icon: <MdAccountTree className="text-lg text-secondary" />,
        title: `Fluxo "${run.flow.name}" em andamento`,
        detail: `${waiting}${until}`,
    }
}

/**
 * Faixa acima do campo de mensagem: fluxos em andamento e mensagens agendadas do lead
 * desta conversa, com Cancelar. Some quando não há nada.
 */
export default function ChatFlowStrip({ runs, onChanged }: { runs: ChatFlowRun[]; onChanged: () => void }) {
    const [toCancel, setToCancel] = useState<ChatFlowRun | null>(null)
    const [cancelling, setCancelling] = useState(false)

    if (runs.length === 0) return null

    const confirmCancel = async () => {
        if (!toCancel) return
        setCancelling(true)
        try {
            const res = await cancelSalesFlowRun(toCancel.id)
            if (res.success) {
                toast.success(toCancel.flow.systemKey ? 'Agendamento cancelado' : 'Fluxo cancelado para este lead')
            } else {
                toast.error(res.error || 'Não foi possível cancelar')
            }
        } catch {
            toast.error('Não foi possível cancelar')
        } finally {
            setCancelling(false)
            setToCancel(null)
            onChanged()
        }
    }

    const isScheduled = toCancel?.flow.systemKey === SALES_FLOW_SYSTEM_KEYS.SCHEDULED_MESSAGE

    return (
        <>
            <div className="mb-3 flex flex-col gap-1.5">
                {runs.map(run => {
                    const { icon, title, detail } = describe(run)
                    return (
                        <div key={run.id} className="flex items-center gap-3 rounded-lg border border-base-300 bg-base-200/50 px-3 py-2">
                            <span className="shrink-0">{icon}</span>
                            <span className="min-w-0 flex-1">
                                <span className="block truncate text-xs font-medium text-base-content">{title}</span>
                                {detail && <span className="block truncate text-xs text-base-content/60">{detail}</span>}
                            </span>
                            <button
                                type="button"
                                className="btn btn-ghost btn-xs gap-1 text-error"
                                onClick={() => setToCancel(run)}
                                title="Cancelar"
                            >
                                <MdClose className="text-sm" />
                                Cancelar
                            </button>
                        </div>
                    )
                })}
            </div>

            <ConfirmModal
                isOpen={!!toCancel}
                onClose={() => setToCancel(null)}
                onConfirm={confirmCancel}
                title={isScheduled ? 'Cancelar mensagem agendada' : 'Cancelar fluxo'}
                message={
                    isScheduled
                        ? 'A mensagem agendada não será enviada.'
                        : `O fluxo "${toCancel?.flow.name ?? ''}" para de executar para este lead. Os passos que já rodaram não são desfeitos.`
                }
                confirmButtonText={isScheduled ? 'Cancelar agendamento' : 'Cancelar fluxo'}
                cancelButtonText="Voltar"
                confirmButtonClass="btn btn-error !border-0 !rounded-lg"
                loading={cancelling}
            />
        </>
    )
}
